import React from 'react';
import ReactDOM from 'react-dom';
import Form from 'react-bootstrap/Form';
import QualdoDataTable from '../../components/bootstrapTable';
import { Edit2, Trash2, Database, Tag, ChevronRight, Check, Pencil, Workflow } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { getDatasourceIcon } from '../../utils/configure_utils';
import RcTooltip from 'rc-tooltip';
import WorkflowModal from '../../components/WorkflowModal';

class RightTableSection extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedMappings: new Set(),
            headerCheckboxContainer: null,
            showWorkflowModal: false,
            workflowRow: null
        };
    }

    componentDidMount() {
        this.updateHeaderCheckboxContainer();
        this.applyBreadcrumbColspan();
    }

    componentDidUpdate(prevProps) {
        this.updateHeaderCheckboxContainer();
        this.applyBreadcrumbColspan();

        if (prevProps.mappings !== this.props.mappings && this.state.selectedMappings.size > 0) {
            const validIds = new Set(this.props.mappings.map(m => m.id));
            let changed = false;
            const nextSel = new Set();
            this.state.selectedMappings.forEach(id => {
                if (validIds.has(id)) nextSel.add(id);
                else changed = true;
            });
            if (changed) {
                this.setState({ selectedMappings: nextSel });
            }
        }
    }

    updateHeaderCheckboxContainer() {
        const container = document.querySelector("#domain-mapping-table-wrapper th:nth-child(1)");
        if (container && this.state.headerCheckboxContainer !== container) {
            this.setState({ headerCheckboxContainer: container });
        }
    }

    applyBreadcrumbColspan = () => {
        setTimeout(() => {
            const breadcrumbRow = document.querySelector(".dmx-breadcrumb-row");
            if (breadcrumbRow) {
                const cells = breadcrumbRow.querySelectorAll("td");
                if (cells.length > 2) {
                    cells[2].setAttribute("colspan", "9");
                }
            }
        }, 150);
    };



    handleSelectRow = (row, checked) => {
        const ids = (row && row.mappingIds && row.mappingIds.length)
            ? row.mappingIds
            : [row.id];
        const nextSel = new Set(this.state.selectedMappings);
        ids.forEach(id => {
            if (checked) nextSel.add(id);
            else nextSel.delete(id);
        });
        this.setState({ selectedMappings: nextSel });
    };

    handleSelectAll = (checked, pageRows) => {
        const nextSel = new Set(this.state.selectedMappings);
        pageRows.forEach(row => {
            if (!row.isGroupHeader && !row.isDummy && !row.isBreadcrumbHeader) {
                const ids = (row.mappingIds && row.mappingIds.length) ? row.mappingIds : [row.id];
                ids.forEach(id => {
                    if (checked) nextSel.add(id);
                    else nextSel.delete(id);
                });
            }
        });
        this.setState({ selectedMappings: nextSel });
    };

    clearSelection = () => {
        this.setState({ selectedMappings: new Set() });
    };

    handleBulkDeleteClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkDelete) {
            this.props.onBulkDelete(ids);
        }
    };

    handleBulkEnableClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkEnable) {
            this.props.onBulkEnable(ids);
            this.clearSelection();
        }
    };

    handleBulkDisableClick = () => {
        const ids = [...this.state.selectedMappings];
        if (ids.length && this.props.onBulkDisable) {
            this.props.onBulkDisable(ids);
            this.clearSelection();
        }
    };

    openWorkflow = (row) => {
        const asset = this.props.indexes
            ? this.props.indexes.getAsset(row.assetId)
            : this.props.assets.find(item => item.id === row.assetId);

        this.setState({
            showWorkflowModal: true,
            workflowRow: {
                ...row,
                kpiName: row.kpiName,
                formula: row.formula,
                datasource: asset ? asset.datasource : '',
                datasourceName: asset ? asset.datasource : '',
                dataset: asset ? asset.dataset : '',
                assetName: asset ? asset.dataset : '',
                attribute: asset ? asset.attribute : '',
                attributeName: asset ? asset.attribute : ''
            }
        });
    };

    getBreadcrumbs = () => {
        const { selectedNode, businesses, functions, kpis, models } = this.props;
        if (!selectedNode || !selectedNode.type) {
            return [{ type: 'ALL', name: 'All Hierarchies' }];
        }

        const path = [];

        if (selectedNode.type === 'business') {
            const b = businesses.find(item => item.id === selectedNode.id);
            if (b) {
                path.push({ type: 'BIZ', name: b.name });
            }
        } else if (selectedNode.type === 'function') {
            const f = functions.find(item => item.id === selectedNode.id);
            if (f) {
                const b = businesses.find(item => item.id === f.businessId);
                if (b) path.push({ type: 'BIZ', name: b.name });
                path.push({ type: 'FN', name: f.name });
            }
        } else if (selectedNode.type === 'kpi') {
            const k = kpis.find(item => item.id === selectedNode.id);
            if (k) {
                const f = functions.find(item => item.id === k.functionId);
                if (f) {
                    const b = businesses.find(item => item.id === f.businessId);
                    if (b) path.push({ type: 'BIZ', name: b.name });
                    path.push({ type: 'FN', name: f.name });
                }
                path.push({ type: 'KPI', name: k.name });
            }
        } else if (selectedNode.type === 'model') {
            const mdl = models.find(item => item.id === selectedNode.id);
            if (mdl) {
                const f = functions.find(item => item.id === mdl.functionId);
                if (f) {
                    const b = businesses.find(item => item.id === f.businessId);
                    if (b) path.push({ type: 'BIZ', name: b.name });
                    path.push({ type: 'FN', name: f.name });
                }
                path.push({ type: 'MDL', name: mdl.name });
            }
        }

        return path;
    };

    renderStatusPill = (status) => {
        if (status === 'manual') {
            return (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-medium tw-text-[#334DB7] tw-bg-[#E6E9F6] tw-border tw-border-solid tw-border-[#B0BAE3] tw-rounded-full">
                    <Pencil size={12} className="tw-text-[#334DB7]" />
                    Manual
                </span>
            );
        }
        return (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-medium tw-text-[#15803D] tw-bg-[#DCFAE6] tw-border tw-border-solid tw-border-[#BBF7D0] tw-rounded-full">
                <Check size={12} className="tw-text-[#15803D]" />
                Auto-mapped
            </span>
        );
    };

    customGetActionComponent = (actionType, headerValue, cell, tableProps, rowIndex) => {
        const rowDataList = tableProps.data?.data;
        if (!rowDataList || !rowDataList[rowIndex]) return null;

        const row = rowDataList[rowIndex][7]?.row;
        if (!row) return null;

        if (row.isDummy) {
            const cleanHeader = headerValue.replace(' ⇅', '').trim();
            if (cleanHeader === "") {
                return <span className="dmx-dummy-marker" />;
            }
            return null;
        }

        const cleanHeader = headerValue.replace(' ⇅', '').trim();

        // Breadcrumb row formatting — must render in "KPIs & Models"
        // (CSS keeps only nth-child(3) visible on .dmx-breadcrumb-row)
        if (row.isBreadcrumbHeader) {
            if (cleanHeader === "KPIs") {
                return (
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        {row.breadcrumbs.map((item, idx) => (
                            <React.Fragment key={idx}>
                                {idx > 0 && <span className="tw-text-gray-400 dark:tw-text-[#80838e] tw-text-sm">/</span>}
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    {item.type !== 'ALL' && (
                                        <span 
                                            className={`tw-text-[#788097] dark:tw-text-white tw-shrink-0`}
                                        >
                                            {
                                                item.type === 'BIZ' ? 
                                                    <>
                                                        <svg className='tw-block' xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <g>
                                                                <path d="M9.43582 5.9987C9.26448 5.9987 9.12137 5.9412 9.00648 5.8262C8.89148 5.71131 8.83398 5.56692 8.83398 5.39303V2.93786C8.83398 2.76386 8.89193 2.61942 9.00782 2.50453C9.12371 2.38953 9.26726 2.33203 9.43848 2.33203H13.0655C13.2368 2.33203 13.3799 2.38953 13.4948 2.50453C13.6098 2.61942 13.6673 2.76386 13.6673 2.93786V5.39303C13.6673 5.56692 13.6094 5.71131 13.4935 5.8262C13.3776 5.9412 13.234 5.9987 13.0628 5.9987H9.43582ZM2.93582 8.33203C2.76448 8.33203 2.62137 8.27453 2.50648 8.15953C2.39148 8.04453 2.33398 7.90203 2.33398 7.73203V2.93186C2.33398 2.76186 2.39193 2.61942 2.50782 2.50453C2.62371 2.38953 2.76726 2.33203 2.93848 2.33203H6.56548C6.73682 2.33203 6.87993 2.38953 6.99482 2.50453C7.10982 2.61953 7.16732 2.76203 7.16732 2.93203V7.7322C7.16732 7.9022 7.10937 8.04464 6.99348 8.15953C6.8776 8.27453 6.73404 8.33203 6.56282 8.33203H2.93582ZM9.43582 13.6654C9.26448 13.6654 9.12137 13.6079 9.00648 13.4929C8.89148 13.3779 8.83398 13.2354 8.83398 13.0654V8.2652C8.83398 8.0952 8.89193 7.95275 9.00782 7.83786C9.12371 7.72286 9.26726 7.66536 9.43848 7.66536H13.0655C13.2368 7.66536 13.3799 7.72286 13.4948 7.83786C13.6098 7.95286 13.6673 8.09536 13.6673 8.26536V13.0655C13.6673 13.2355 13.6094 13.378 13.4935 13.4929C13.3776 13.6079 13.234 13.6654 13.0628 13.6654H9.43582ZM2.93582 13.6654C2.76448 13.6654 2.62137 13.6079 2.50648 13.4929C2.39148 13.378 2.33398 13.2335 2.33398 13.0595V10.6044C2.33398 10.4305 2.39193 10.2861 2.50782 10.1712C2.62371 10.0562 2.76726 9.9987 2.93848 9.9987H6.56548C6.73682 9.9987 6.87993 10.0562 6.99482 10.1712C7.10982 10.2861 7.16732 10.4305 7.16732 10.6044V13.0595C7.16732 13.2335 7.10937 13.378 6.99348 13.4929C6.8776 13.6079 6.73404 13.6654 6.56282 13.6654H2.93582Z" fill="currentColor" />
                                                            </g>
                                                        </svg>
                                                    </>
                                                :
                                                    item.type === 'FN' ? 
                                                        <>
                                                            <svg className='tw-block' xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <g>
                                                                    <path d="M3.077 12.6654C2.77011 12.6654 2.51389 12.5626 2.30833 12.357C2.10278 12.1515 2 11.8953 2 11.5884V4.40903C2 4.10214 2.10278 3.84592 2.30833 3.64036C2.51389 3.43481 2.77011 3.33203 3.077 3.33203H5.95C6.09356 3.33203 6.23311 3.36109 6.36867 3.4192C6.50411 3.47731 6.62011 3.55464 6.71667 3.6512L7.73083 4.66536H12.923C13.2299 4.66536 13.4861 4.76814 13.6917 4.9737C13.8972 5.17925 14 5.43548 14 5.74236V11.5884C14 11.8953 13.8972 12.1515 13.6917 12.357C13.4861 12.5626 13.2299 12.6654 12.923 12.6654H3.077Z" fill="currentColor" />
                                                                </g>
                                                            </svg>
                                                        </>
                                                :
                                                    item.type === 'KPI' ? 
                                                            <>
                                                                <svg className='tw-block' xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
                                                                    <path d="M14.7695 3.375C15.1483 3.37508 15.469 3.50613 15.7314 3.76855C15.9939 4.03098 16.1249 4.35174 16.125 4.73047V13.2695C16.1249 13.6483 15.9939 13.969 15.7314 14.2314C15.469 14.4939 15.1483 14.6249 14.7695 14.625H3.23047C2.85174 14.6249 2.53098 14.4939 2.26855 14.2314C2.00613 13.969 1.87508 13.6483 1.875 13.2695V4.73047C1.87508 4.35174 2.00613 4.03098 2.26855 3.76855C2.53098 3.50613 2.85174 3.37508 3.23047 3.375H14.7695ZM4.5 6.75V10.8281H5.49316V9.02734L6.86426 10.8281H8.0791L6.44043 8.74902L8.02051 6.75H6.85254L5.49316 8.53906V6.75H4.5ZM8.55078 10.8281H9.54395V9.36426H10.1602C11.1999 9.36426 11.659 8.73726 11.6592 8.06348C11.6592 7.314 11.1595 6.75 10.1602 6.75H8.55078V10.8281ZM12.1768 10.8281H13.1699V6.75H12.1768V10.8281ZM10.085 7.55176C10.4799 7.55183 10.6484 7.7498 10.6484 8.06348C10.6483 8.3769 10.4797 8.57414 10.085 8.57422H9.54395V7.55176H10.085Z" fill="currentColor" />
                                                                </svg>
                                                            </>
                                                :''      
                                            }
                                        </span>
                                    )}
                                    <span className="tw-text-[14px] tw-font-medium tw-text-[#181D27] dark:tw-text-white">
                                        {item.name}
                                    </span>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                );
            }
            return null;
        }

        // Regular Row formatting
        switch (cleanHeader) {
            case "": 
                return (
                    <label className="tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-mb-0">
                        <span className={`tw-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-transition-colors tw-flex-shrink-0 ${
                            (row.mappingIds || [row.id]).every(id => this.state.selectedMappings.has(id))
                                ? 'tw-bg-blue-600 tw-border-blue-600'
                                : 'tw-bg-white tw-border-[#D5D7DA] dark:tw-bg-[#1E293B] dark:tw-border-[#475569]'
                        }`}>
                            {(row.mappingIds || [row.id]).every(id => this.state.selectedMappings.has(id)) && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </span>
                        <input
                            type="checkbox"
                            checked={(row.mappingIds || [row.id]).every(id => this.state.selectedMappings.has(id))}
                            onChange={(e) => this.handleSelectRow(row, e.target.checked)}
                            className="tw-hidden"
                        />
                    </label>
                );
            case "Actions":
                return (
                    <div className="tw-inline-flex tw-items-center tw-gap-1.5">
                        <button 
                            type="button"
                            onClick={() => this.props.onDeleteMapping(row.mappingIds || row.id)}
                            className="tw-p-1 tw-text-[#A4A7AE] hover:tw-text-red-600 hover:tw-bg-gray-100 dark:hover:tw-bg-white/10 tw-rounded tw-transition-colors tw-border-none tw-bg-transparent"
                            title="Delete mapping"
                        >
                            <Trash2 size={16} />
                        </button>
                        <button 
                            type="button"
                            onClick={() => this.openWorkflow(row)}
                            className="tw-p-1 tw-text-[#A4A7AE] hover:tw-text-blue-600 hover:tw-bg-gray-100 dark:hover:tw-bg-white/10 tw-rounded tw-transition-colors tw-border-none tw-bg-transparent"
                            title="View workflow"
                        >
                            <Workflow size={16} />
                        </button>
                        <button 
                            type="button"
                            onClick={() => this.props.onEditMapping(row)}
                            className="tw-p-1 tw-text-[#A4A7AE] hover:tw-text-blue-600 hover:tw-bg-gray-100 dark:hover:tw-bg-white/10 tw-rounded tw-transition-colors tw-border-none tw-bg-transparent"
                            title="Edit mapping"
                        >
                            <Edit2 size={16} />
                        </button>
                    </div>
                );
            case "Enable":
                return (
                    <Form.Check 
                        type="switch"
                        id={`enable-switch-${row.id}`}
                        checked={row.enabled}
                        onChange={() => this.props.onToggleEnable(row.mappingIds || row.id)}
                        className="tw-cursor-pointer"
                    />
                );
            case "KPIs":
                return (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {row.isModel ? (
                            <span className="tw-text-[#788097] dark:tw-text-white tw-shrink-0">
                                <svg className='tw-block' xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M7.39815 14.1377L2.93665 11.5697C2.74687 11.4604 2.59898 11.3143 2.49298 11.1314C2.38698 10.9484 2.33398 10.7475 2.33398 10.5287V5.47239C2.33398 5.25361 2.38698 5.05272 2.49298 4.86972C2.59898 4.68683 2.74687 4.54072 2.93665 4.43139L7.39815 1.86339C7.58782 1.75394 7.78865 1.69922 8.00065 1.69922C8.21265 1.69922 8.41348 1.75394 8.60315 1.86339L13.0647 4.43139C13.2544 4.54072 13.4023 4.68683 13.5083 4.86972C13.6143 5.05272 13.6673 5.25361 13.6673 5.47239V10.5287C13.6673 10.7475 13.6143 10.9484 13.5083 11.1314C13.4023 11.3143 13.2544 11.4604 13.0647 11.5697L8.60315 14.1377C8.41348 14.2472 8.21265 14.3019 8.00065 14.3019C7.78865 14.3019 7.58782 14.2472 7.39815 14.1377ZM7.50065 8.28772V13.0467L7.91082 13.2826C7.94082 13.2997 7.97076 13.3082 8.00065 13.3082C8.03054 13.3082 8.06048 13.2997 8.09048 13.2826L8.50065 13.0467V8.28772L12.6673 5.87489V5.44422C12.6673 5.42277 12.662 5.40139 12.6513 5.38005C12.6406 5.35872 12.6246 5.34161 12.6032 5.32872L12.1173 5.04672L8.00065 7.42622L3.88398 5.04672L3.39815 5.32872C3.37671 5.34161 3.36065 5.35872 3.34998 5.38005C3.33932 5.40139 3.33398 5.42277 3.33398 5.44422V5.87489L7.50065 8.28772Z" fill="currentColor" />
                                </svg>
                            </span>
                        ) : (
                                <span className="tw-text-[#788097] dark:tw-text-white tw-shrink-0">
                                    <svg className='tw-block' xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
                                        <path d="M14.7695 3.375C15.1483 3.37508 15.469 3.50613 15.7314 3.76855C15.9939 4.03098 16.1249 4.35174 16.125 4.73047V13.2695C16.1249 13.6483 15.9939 13.969 15.7314 14.2314C15.469 14.4939 15.1483 14.6249 14.7695 14.625H3.23047C2.85174 14.6249 2.53098 14.4939 2.26855 14.2314C2.00613 13.969 1.87508 13.6483 1.875 13.2695V4.73047C1.87508 4.35174 2.00613 4.03098 2.26855 3.76855C2.53098 3.50613 2.85174 3.37508 3.23047 3.375H14.7695ZM4.5 6.75V10.8281H5.49316V9.02734L6.86426 10.8281H8.0791L6.44043 8.74902L8.02051 6.75H6.85254L5.49316 8.53906V6.75H4.5ZM8.55078 10.8281H9.54395V9.36426H10.1602C11.1999 9.36426 11.659 8.73726 11.6592 8.06348C11.6592 7.314 11.1595 6.75 10.1602 6.75H8.55078V10.8281ZM12.1768 10.8281H13.1699V6.75H12.1768V10.8281ZM10.085 7.55176C10.4799 7.55183 10.6484 7.7498 10.6484 8.06348C10.6483 8.3769 10.4797 8.57414 10.085 8.57422H9.54395V7.55176H10.085Z" fill="currentColor" />
                                    </svg>
                            </span>
                        )}
                        <span className="tw-text-[#181D27] tw-text-[14px] tw-font-semibold dark:tw-text-white">
                            {row.kpiName}
                        </span>
                    </div>
                );
            case "Formula":
                if (row.isModel) {
                    return (
                        <span className="tw-text-xs tw-text-purple-600 dark:tw-text-purple-400 tw-font-semibold tw-bg-purple-50 dark:tw-bg-purple-500/10 tw-px-2 tw-py-0.5 tw-rounded">
                            {row.modelKind}
                        </span>
                    );
                }
                if (row.formula) {
                    return (
                        <RcTooltip
                            placement="top"
                            overlay={
                                <div style={{ wordBreak: 'break-all', maxWidth: '300px' }}>
                                    {row.formula}
                                </div>
                            }
                            arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                        >
                            <code 
                                className="tw-block tw-text-[12px] tw-font-medium tw-text-[#404040] dark:tw-text-[#E2E8F0] tw-bg-[#F5F5F5] dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-white/10 tw-rounded-[6px] tw-px-2 tw-py-1 tw-max-w-[170px] tw-truncate"
                            >
                                {row.formula}
                            </code>
                        </RcTooltip>
                    );
                }
                return <span className="tw-text-xs tw-text-gray-300">—</span>;
            case "Datasources": {
                const datasources = row.datasources && row.datasources.length
                    ? row.datasources
                    : (() => {
                        const as = this.props.assets.find(item => item.id === row.assetId);
                        return as ? [as.datasource] : [];
                    })();
                if (!datasources.length) {
                    return <span className="tw-text-xs tw-text-gray-300">—</span>;
                }
                return (
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                        {datasources.map(ds => (
                            <div
                                key={ds}
                                className="tw-inline-flex tw-items-center tw-px-2 tw-py-[2px] tw-rounded-[6px] tw-border tw-border-solid tw-border-[#B2DDFF] dark:tw-border-white/10 tw-bg-[#F3F9FF] dark:tw-bg-[#1E1E2D]"
                            >
                                {getDatasourceIcon(ds)}
                                &nbsp;<span className="tw-text-xs tw-font-medium tw-text-[#175CD3] dark:tw-text-[#53B1FD] tw-uppercase">{ds}</span>
                            </div>
                        ))}
                    </div>
                );
            }
            case "Mapped Data fields": {
                const fieldAssets = row.mappedAssets && row.mappedAssets.length
                    ? row.mappedAssets
                    : (() => {
                        const as = this.props.assets.find(item => item.id === row.assetId);
                        return as ? [as] : [];
                    })();
                if (!fieldAssets.length) {
                    return <span className="tw-text-xs tw-text-gray-300">—</span>;
                }

                const datasets = [...new Set(fieldAssets.map(a => a.dataset).filter(Boolean))];
                const attrNames = fieldAssets.map(a => {
                    const parts = String(a.attribute || '').split('.');
                    return parts[parts.length - 1] || a.attribute;
                }).filter(Boolean);
                const preview = attrNames.slice(0, 3).join(', ');
                const extra = attrNames.length > 3 ? ` +${attrNames.length - 3} more` : '';
                const overlayText = [
                    datasets.length ? `Datasets (${datasets.length}): ${datasets.join(', ')}` : '',
                    attrNames.length ? `Attributes (${attrNames.length}): ${attrNames.join(', ')}` : ''
                ].filter(Boolean).join('\n');

                return (
                    <RcTooltip
                        placement="top"
                        overlay={
                            <div style={{ whiteSpace: 'pre-wrap', maxWidth: '360px', wordBreak: 'break-word' }}>
                                {overlayText}
                            </div>
                        }
                        arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                    >
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-left tw-cursor-default">
                            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-bg-white dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#D4D4D4] dark:tw-border-white/10 tw-rounded-[6px] tw-flex-shrink-0">
                                <Tag size={16} className="tw-text-[#181D27] dark:tw-text-white" />
                            </span>
                            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                                <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-[#252B37] dark:tw-text-white tw-my-0 tw-truncate">
                                    {attrNames.length} attribute{attrNames.length === 1 ? '' : 's'}
                                    {extra ? <span className="tw-font-medium tw-text-[#717680]"> {extra}</span> : null}
                                </span>
                                <span className="tw-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-medium tw-text-[#717680] dark:tw-text-gray-400 tw-my-0 tw-truncate">
                                    <Database size={10} className="tw-text-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                                    {datasets.length} dataset{datasets.length === 1 ? '' : 's'}
                                    {preview ? (
                                        <>
                                            <ChevronRight size={10} className="tw-[#A4A7AE] dark:tw-text-[#80838e] tw-shrink-0" />
                                            <span className="tw-truncate">{preview}</span>
                                        </>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                    </RcTooltip>
                );
            }
            case "Status":
                return this.renderStatusPill(row.status);
            default:
                return null;
        }
    };

    customRowHighlighter = (row) => {
        const rowItem = row.Actions?.row;
        if (rowItem) {
            if (rowItem.isBreadcrumbHeader) {
                return 'tbl-row tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 dark:tw-border-white/5 hover:tw-bg-gray-50/50 dark:hover:tw-bg-white/[0.01] dmx-breadcrumb-row';
            }
        }
        return 'tbl-row tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 dark:tw-border-white/5 hover:tw-bg-gray-50/50 dark:hover:tw-bg-white/[0.01]';
    };

    render() {
        const { 
            businesses, 
            functions, 
            kpis, 
            models, 
            mappings, 
            selectedNode,
            indexes: indexesProp
        } = this.props;

        const indexes = indexesProp || null;

        const resolveCtx = (m) => {
            if (indexes) return indexes.resolveMappingContext(m);
            const k = kpis.find(item => item.id === m.kpiId);
            const mdl = k ? null : models.find(item => item.id === m.modelId);
            const item = k || mdl;
            if (!item) return null;
            const f = functions.find(item => item.id === item.functionId);
            const b = f ? businesses.find(item => item.id === f.businessId) : null;
            return { kpi: k, model: mdl, item, fn: f, biz: b, isModel: !!mdl, effectivelyActive: true };
        };

        const filteredRows = mappings.filter(m => {
            const ctx = resolveCtx(m);
            if (!ctx || !ctx.item) return false;

            const { biz: b, fn: f, kpi: k, model: mdl } = ctx;

            if (selectedNode.type === 'business' && b && b.id !== selectedNode.id) return false;
            if (selectedNode.type === 'function' && f && f.id !== selectedNode.id) return false;
            if (selectedNode.type === 'kpi' && k && k.id !== selectedNode.id) return false;
            if (selectedNode.type === 'model' && mdl && mdl.id !== selectedNode.id) return false;

            return true;
        });

        const fullRowsWithGroups = [];

        if (selectedNode && selectedNode.type) {
            const breadcrumbs = this.getBreadcrumbs();
            fullRowsWithGroups.push({
                id: 'table_breadcrumb_row',
                isBreadcrumbHeader: true,
                breadcrumbs: breadcrumbs
            });
        }

        // Group mapping rows by KPI/model name (one table row per unique name)
        const groupedByKpi = new Map();
        filteredRows.forEach(m => {
            const ctx = resolveCtx(m);
            if (!ctx || !ctx.item) return;
            const nameKey = String(ctx.item.name || '')
                .trim()
                .toLowerCase();
            const key = nameKey || m.kpiId || m.modelId || m.id;
            if (!groupedByKpi.has(key)) {
                groupedByKpi.set(key, { mappings: [], ctx });
            }
            groupedByKpi.get(key).mappings.push(m);
        });

        const groupedList = Array.from(groupedByKpi.entries()).map(([key, group]) => {
            const { mappings: groupMappings, ctx } = group;
            const { item, fn: f, biz: b, kpi: k, model: mdl } = ctx;
            const mappedAssets = groupMappings
                .map(gm => {
                    if (indexes) return indexes.getAsset(gm.assetId);
                    return this.props.assets.find(a => a.id === gm.assetId);
                })
                .filter(Boolean);
            // Deduplicate assets by id so the same field isn't listed twice
            const uniqueAssetsById = new Map();
            mappedAssets.forEach(a => {
                if (a && a.id != null && !uniqueAssetsById.has(a.id)) {
                    uniqueAssetsById.set(a.id, a);
                }
            });
            const uniqueMappedAssets = Array.from(uniqueAssetsById.values());
            const datasources = [...new Set(uniqueMappedAssets.map(a => a.datasource).filter(Boolean))];
            const status = groupMappings.some(gm => gm.status === 'manual')
                ? 'manual'
                : (groupMappings[0] && groupMappings[0].status) || 'unmapped';
            const enabled = groupMappings.every(gm => gm.enabled);
            const kpiIds = [...new Set(groupMappings.map(gm => gm.kpiId).filter(Boolean))];
            const modelIds = [...new Set(groupMappings.map(gm => gm.modelId).filter(Boolean))];

            return {
                id: `kpi_group_${key}`,
                kpiId: k ? k.id : (groupMappings[0] && groupMappings[0].kpiId),
                modelId: mdl ? mdl.id : undefined,
                kpiIds,
                modelIds,
                assetId: groupMappings[0] && groupMappings[0].assetId,
                mappingIds: groupMappings.map(gm => gm.id),
                mappedAssets: uniqueMappedAssets,
                datasources,
                status,
                enabled,
                isGroupHeader: false,
                isKpiGroup: true,
                kpiName: item.name,
                isModel: !!mdl,
                modelKind: mdl ? mdl.kind : '',
                formula: k ? k.formula : '',
                bizName: b ? b.name : '',
                fnName: f ? f.name : ''
            };
        });

        // Stable sort: business → function → KPI name
        groupedList.sort((a, b) => {
            const bizCmp = (a.bizName || '').localeCompare(b.bizName || '');
            if (bizCmp !== 0) return bizCmp;
            const fnCmp = (a.fnName || '').localeCompare(b.fnName || '');
            if (fnCmp !== 0) return fnCmp;
            return (a.kpiName || '').localeCompare(b.kpiName || '');
        });

        groupedList.forEach(row => fullRowsWithGroups.push(row));

        const formattedRows = fullRowsWithGroups.map((rowItem) => {
            return [
                { type: "td", value: (rowItem.mappingIds || [rowItem.id]).every(id => this.state.selectedMappings.has(id)) ? "checked" : "unchecked", row: rowItem },
                { type: "td", value: rowItem.enabled ? "enabled" : "disabled", row: rowItem },
                { type: "td", value: rowItem.kpiName || "", row: rowItem },
                { type: "td", value: rowItem.formula || rowItem.modelKind || "", row: rowItem },
                { type: "td", value: (rowItem.datasources || []).join(',') || "", row: rowItem },
                { type: "td", value: rowItem.mappedAssets ? String(rowItem.mappedAssets.length) : "", row: rowItem },
                { type: "td", value: rowItem.status || "", row: rowItem },
                { type: "component", value: "", row: rowItem }
            ];
        });

        const realRowsCount = formattedRows.length;
        const targetLimit = 15;
        if (realRowsCount > 0 && realRowsCount <= targetLimit) {
            const padNeeded = (targetLimit + 1) - realRowsCount;
            for (let i = 0; i < padNeeded; i++) {
                const dummyRow = { id: `dummy_pad_${i}`, isDummy: true, isGroupHeader: false };
                formattedRows.push([
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "td", value: "", row: dummyRow },
                    { type: "component", value: "", row: dummyRow }
                ]);
            }
        }

        const headers = ["", "Enable", "KPIs", "Formula", "Datasources", "Mapped Data fields", "Status", "Actions"];

        const tableData = {
            headers: headers,
            data: formattedRows,
            not_sortable: true
        };

        const pageOptions = [15, 25, 50, 100];

        const actualRowsToSelect = groupedList;
        const isAllSelected = actualRowsToSelect.length > 0 && actualRowsToSelect.every(row =>
            (row.mappingIds || [row.id]).every(id => this.state.selectedMappings.has(id))
        );

        const { headerCheckboxContainer, selectedMappings } = this.state;
        const selectedCount = selectedMappings.size;

        return (
            <>
                {/* Custom styling override block for group headers & hiding the prepended ID column */}
                <style>{`
                    .react-bootstrap-table th:first-child,
                    .react-bootstrap-table td:first-child {
                    }
                    .react-bootstrap-table td {
                        vertical-align: middle !important;
                    }
                    /* Select-all checkbox in first header column */
                    #domain-mapping-table-wrapper th:nth-child(1) {
                        white-space: nowrap !important;
                        vertical-align: middle !important;
                        text-align: center !important;
                        width: 48px !important;
                        min-width: 48px !important;
                        max-width: 48px !important;
                    }
                    #domain-mapping-table-wrapper th:nth-child(1) > label {
                        display: inline-flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        margin: 0 !important;
                    }
                    #domain-mapping-table-wrapper td:nth-child(1) {
                        text-align: center !important;
                        width: 48px !important;
                        min-width: 48px !important;
                        max-width: 48px !important;
                    }
                    .dmx-breadcrumb-row td {
                        background-color: #EEF1FB !important;
                        border-top: 1px solid #DCE2F4 !important;
                        border-bottom: 1px solid #DCE2F4 !important;
                        padding-top: 8px !important;
                        padding-bottom: 8px !important;
                        border-left: none !important;
                        border-right: none !important;
                    }
                    .dmx-breadcrumb-row td:not(:nth-child(3)) {
                        display: none !important;
                    }

                    /* Hide dummy padding rows */
                    .react-bootstrap-table tr:has(.dmx-dummy-marker) {
                        display: none !important;
                    }

                    /* Dark Theme Overrides */
                    .dark .react-bootstrap-table input[type="checkbox"],
                    .dark #domain-mapping-table-wrapper input[type="checkbox"] {
                        color-scheme: dark !important;
                    }
                    .dark .dmx-breadcrumb-row td {
                        background-color: rgba(255, 255, 255, 0.03) !important;
                        border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
                        border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                    }

                    /* Hide the label text and sorting controls of the last header cell (Actions) */
                    #domain-mapping-table-wrapper th:last-child {
                        color: transparent !important;
                    }
                    #domain-mapping-table-wrapper th:last-child .order,
                    #domain-mapping-table-wrapper th:last-child .caret {
                        display: none !important;
                    }
                `}</style>

                {realRowsCount <= targetLimit && (
                    <style>{`
                        .react-bootstrap-table__footer {
                            display: none !important;
                        }
                    `}</style>
                )}

                {selectedCount > 0 && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-[10px] tw-mb-2 tw-bg-blue-50 dark:tw-bg-[#13131F] tw-border-0 tw-border-b tw-border-solid tw-border-[#ECECEC] dark:tw-border-[#334155]">
                        {/* Left: blue pill + label */}
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-font-semibold tw-text-white tw-bg-[#2563eb] dark:tw-bg-[#3B82F6] tw-rounded-full tw-pl-1.5 tw-pr-2.5 tw-py-1">
                                <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-full tw-bg-white/30 dark:tw-bg-white/20 tw-text-[11px] tw-font-bold">{selectedCount}</span>
                                <span className="tw-font-semibold">selected</span>
                            </span>
                            <span className="tw-text-xs tw-font-medium tw-text-[#6B7280] dark:tw-text-[#94A3B8]">
                                mapping{selectedCount === 1 ? '' : 's'}
                            </span>
                        </div>

                        {/* Right: action buttons */}
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {/* Enable */}
                            <button
                                type="button"
                                onClick={this.handleBulkEnableClick}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#334059] dark:tw-text-[#E2E8F0] tw-bg-white dark:tw-bg-[#1E293B] tw-border tw-border-solid tw-border-[#D1D5DB] dark:tw-border-[#334155] tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#273449] hover:tw-border-[#9CA3AF] dark:hover:tw-border-[#4B5563] tw-transition-colors"
                                title="Enable selected mappings"
                            >
                                <FontAwesomeIcon icon={faToggleOn} className="tw-text-[#A4A7AE] dark:tw-text-[#34D399]" style={{ fontSize: '18px' }} />
                                Enable
                            </button>
                            {/* Disable */}
                            <button
                                type="button"
                                onClick={this.handleBulkDisableClick}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#334059] dark:tw-text-[#E2E8F0] tw-bg-white dark:tw-bg-[#1E293B] tw-border tw-border-solid tw-border-[#D1D5DB] dark:tw-border-[#334155] tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#273449] hover:tw-border-[#9CA3AF] dark:hover:tw-border-[#4B5563] tw-transition-colors"
                                title="Disable selected mappings"
                            >
                                <FontAwesomeIcon icon={faToggleOff} className="tw-text-[#A4A7AE] dark:tw-text-[#94A3B8]" style={{ fontSize: '18px' }} />
                                Disable
                            </button>
                            {/* Divider */}
                            <span className="tw-w-px tw-h-5 tw-bg-blue-200 dark:tw-bg-[#334155] tw-mx-0.5" aria-hidden="true"></span>
                            {/* Delete */}
                            <button
                                type="button"
                                onClick={this.handleBulkDeleteClick}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-[#D92D20] dark:tw-bg-[#B91C1C] tw-border-none tw-rounded-md tw-cursor-pointer hover:tw-bg-[#B42318] dark:hover:tw-bg-[#991B1B] tw-transition-colors"
                                title="Delete selected mappings"
                            >
                                <Trash2 size={15} />
                                Delete
                            </button>
                            {/* Close / Clear × */}
                            <button
                                type="button"
                                onClick={this.clearSelection}
                                className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-text-[#6B7280] dark:tw-text-[#94A3B8] tw-bg-transparent tw-border-none tw-rounded-full tw-cursor-pointer hover:tw-bg-blue-100 dark:hover:tw-bg-white/10 hover:tw-text-[#374151] dark:hover:tw-text-white tw-transition-colors tw-p-0"
                                title="Clear selection"
                                aria-label="Clear selection"
                            >
                                <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                <div id="domain-mapping-table-wrapper" className='domain-mapping-table hide-tbl-border-radius tw-mt-[10px]'>
                    <QualdoDataTable
                        id="domainMappingTable"
                        component_name="domainMappingTable"
                        classNameForTable="table"
                        wrapperClasses="table-responsive action"
                        data={tableData}
                        pageOptions={pageOptions}
                        paginationSize={15}
                        customGetActionComponent={this.customGetActionComponent}
                        customRowHighlighter={this.customRowHighlighter}
                    />
                </div>

                {headerCheckboxContainer && ReactDOM.createPortal(
                    <label className="tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-mb-0">
                        <span className={`tw-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-transition-colors tw-flex-shrink-0 ${
                            isAllSelected
                                ? 'tw-bg-blue-600 tw-border-blue-600'
                                : 'tw-bg-white tw-border-[#D5D7DA] dark:tw-bg-[#1E293B] dark:tw-border-[#475569]'
                        }`}>
                            {isAllSelected && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </span>
                        <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={(e) => this.handleSelectAll(e.target.checked, actualRowsToSelect)}
                            className="tw-hidden"
                        />
                    </label>,
                    headerCheckboxContainer
                )}

                <WorkflowModal
                    show={this.state.showWorkflowModal}
                    row={this.state.workflowRow}
                    onClose={() => this.setState({ showWorkflowModal: false, workflowRow: null })}
                />
            </>
        );
    }
}

export default RightTableSection;
